import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Select from '../../components/common/Select';
import Pagination from '../../components/common/Pagination';

interface PayslipDetail {
  label: string;
  amount: number;
}

interface Payslip {
  id: string;
  year: number;
  month: number;
  baseSalary: number;
  positionAllowance: number;
  overtimeAllowance: number;
  transportAllowance: number;
  housingAllowance: number;
  familyAllowance: number;
  otherAllowances: number;
  totalAllowances: number;
  grossSalary: number;
  healthInsurance: number;
  pensionInsurance: number;
  employmentInsurance: number;
  incomeTax: number;
  residentTax: number;
  otherDeductions: number;
  totalDeductions: number;
  netSalary: number;
  workingDays: number;
  workingHours: number;
  overtimeHours: number;
  paidLeaveDays: number;
  status: string;
  paymentDate: string;
  pdfUrl: string;
}

type ViewMode = 'cards' | 'table';

export default function PayslipList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedYear = parseInt(searchParams.get('year') || String(new Date().getFullYear()));
  const setSelectedYear = (year: number) => {
    setSearchParams(prev => { prev.set('year', String(year)); prev.set('page', '1'); return prev; }, { replace: true });
  };
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // ページネーション・ソート・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '12');
  const sortConfig = {
    key: searchParams.get('sortBy') || 'month',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc',
  };
  const setSortConfig = (updater: { key: string; direction: 'asc' | 'desc' } | ((prev: { key: string; direction: 'asc' | 'desc' }) => { key: string; direction: 'asc' | 'desc' })) => {
    const next = typeof updater === 'function' ? updater(sortConfig) : updater;
    setSearchParams(prev => { prev.set('sortBy', next.key); prev.set('sortOrder', next.direction); return prev; }, { replace: true });
  };
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };

  useEffect(() => {
    fetchPayslips();
  }, [selectedYear]);

  const fetchPayslips = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: Payslip[] }>(`/api/payslips?year=${selectedYear}`);
      setPayslips(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch payslips:', error);
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | string | undefined) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(Number(amount || 0));
  };

  const formatNumber = (num: number | undefined) => {
    return new Intl.NumberFormat('ja-JP').format(num || 0);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'success'> = {
      draft: 'default',
      issued: 'warning',
      paid: 'success',
    };
    const labels: Record<string, string> = {
      draft: '作成中',
      issued: '発行済',
      paid: '支払済',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const handleViewDetail = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
    setShowDetailModal(true);
  };

  // 年間集計
  const annualSummary = useMemo(() => {
    return {
      totalGross: payslips.reduce((sum, p) => sum + (p.grossSalary || 0), 0),
      totalNet: payslips.reduce((sum, p) => sum + (p.netSalary || 0), 0),
      totalDeductions: payslips.reduce((sum, p) => sum + (p.totalDeductions || 0), 0),
      totalOvertime: payslips.reduce((sum, p) => sum + (p.overtimeHours || 0), 0),
      monthsWithData: payslips.length,
    };
  }, [payslips]);

  // ソート済みpayslips
  const sortedPayslips = useMemo(() => {
    return [...payslips].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Payslip];
      const bValue = b[sortConfig.key as keyof Payslip];
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return sortConfig.direction === 'asc' 
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [payslips, sortConfig]);

  // ページネーション
  const totalPages = Math.ceil(sortedPayslips.length / pageSize);
  const paginatedPayslips = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedPayslips.slice(start, start + pageSize);
  }, [sortedPayslips, currentPage, pageSize]);

  const defaultSort = { key: 'month', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' as const };
      if (prev.direction === 'asc') return { key, direction: 'desc' as const };
      return defaultSort;
    });
    setCurrentPage(1);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">給与明細</h1>
        <div className="flex gap-2 items-center">
          {/* 表示切替 */}
          <div className="flex border dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              カード
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              一覧
            </button>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
        </div>
      </div>

      {/* 年間集計 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">年間総支給額</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(annualSummary.totalGross)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">年間手取り</p>
          <p className="text-xl font-bold text-blue-600">{formatCurrency(annualSummary.totalNet)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">年間控除合計</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(annualSummary.totalDeductions)}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">年間残業時間</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{formatNumber(annualSummary.totalOvertime)}h</p>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : viewMode === 'cards' ? (
        /* カード表示 */
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {months.map((monthName, index) => {
            const payslip = payslips.find(p => p.month === index + 1);
            return (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{monthName}</h3>
                  {payslip ? getStatusBadge(payslip.status) : <Badge>未発行</Badge>}
                </div>
                {payslip ? (
                  <>
                    <div className="space-y-2 mb-3 sm:mb-4">
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">総支給</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(payslip.grossSalary)}</span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-500">控除計</span>
                        <span className="font-medium text-red-600">-{formatCurrency(payslip.totalDeductions)}</span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm border-t pt-2 dark:border-gray-700">
                        <span className="text-gray-700 dark:text-gray-300 font-medium">手取り</span>
                        <span className="text-blue-600 font-bold">{formatCurrency(payslip.netSalary)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" className="flex-1 text-xs" onClick={() => handleViewDetail(payslip)}>
                        詳細
                      </Button>
                      {payslip.pdfUrl && (
                        <Button variant="secondary" size="sm" className="text-xs" onClick={() => window.open(payslip.pdfUrl, '_blank')}>
                          PDF
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 sm:py-6 text-gray-400">
                    <svg className="w-10 sm:w-12 h-10 sm:h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-xs sm:text-sm">明細がありません</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        /* テーブル表示 */
        <Card>
          {/* 表示件数・ソート */}
          <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>表示件数:</span>
              <Select
                value={pageSize.toString()}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-24"
              >
                <option value="6">6</option>
                <option value="12">12</option>
              </Select>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>並び替え:</span>
              <Select
                value={`${sortConfig.key}-${sortConfig.direction}`}
                onChange={(e) => {
                  const [key, direction] = e.target.value.split('-');
                  setSortConfig({ key, direction: direction as 'asc' | 'desc' });
                  setCurrentPage(1);
                }}
                className="w-40"
              >
                <option value="month-asc">月（昇順）</option>
                <option value="month-desc">月（降順）</option>
                <option value="grossSalary-desc">総支給（多い順）</option>
                <option value="grossSalary-asc">総支給（少ない順）</option>
                <option value="netSalary-desc">手取り（多い順）</option>
                <option value="netSalary-asc">手取り（少ない順）</option>
                <option value="overtimeHours-desc">残業（多い順）</option>
              </Select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b dark:border-gray-700">
                  <th 
                    className="text-left py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('month')}
                  >
                    月 {sortConfig.key === 'month' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('baseSalary')}
                  >
                    基本給 {sortConfig.key === 'baseSalary' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('totalAllowances')}
                  >
                    手当合計 {sortConfig.key === 'totalAllowances' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('grossSalary')}
                  >
                    総支給 {sortConfig.key === 'grossSalary' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('totalDeductions')}
                  >
                    控除合計 {sortConfig.key === 'totalDeductions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('netSalary')}
                  >
                    手取り {sortConfig.key === 'netSalary' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleSort('overtimeHours')}
                  >
                    残業 {sortConfig.key === 'overtimeHours' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-gray-500">状態</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPayslips.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-gray-500">
                      給与明細がありません
                    </td>
                  </tr>
                ) : paginatedPayslips.map((payslip) => {
                  const monthName = months[payslip.month - 1];
                  return (
                    <tr key={payslip.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{monthName}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(payslip.baseSalary)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(payslip.totalAllowances)}</td>
                      <td className="py-3 px-4 text-right font-medium">{formatCurrency(payslip.grossSalary)}</td>
                      <td className="py-3 px-4 text-right text-red-600">-{formatCurrency(payslip.totalDeductions)}</td>
                      <td className="py-3 px-4 text-right font-bold text-blue-600">{formatCurrency(payslip.netSalary)}</td>
                      <td className="py-3 px-4 text-center">
                        {payslip.overtimeHours > 0 ? (
                          <span className="text-orange-600">{payslip.overtimeHours}h</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(payslip.status)}</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetail(payslip)}>詳細</Button>
                          {payslip.pdfUrl && (
                            <Button variant="ghost" size="sm" onClick={() => window.open(payslip.pdfUrl, '_blank')}>PDF</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* 年間合計行 */}
                <tr className="bg-gray-50 dark:bg-gray-800 font-medium">
                  <td className="py-3 px-4 text-gray-900 dark:text-white">年間合計</td>
                  <td className="py-3 px-4 text-right">-</td>
                  <td className="py-3 px-4 text-right">-</td>
                  <td className="py-3 px-4 text-right">{formatCurrency(annualSummary.totalGross)}</td>
                  <td className="py-3 px-4 text-right text-red-600">-{formatCurrency(annualSummary.totalDeductions)}</td>
                  <td className="py-3 px-4 text-right text-blue-600 font-bold">{formatCurrency(annualSummary.totalNet)}</td>
                  <td className="py-3 px-4 text-center text-orange-600">{formatNumber(annualSummary.totalOvertime)}h</td>
                  <td className="py-3 px-4 text-center">-</td>
                  <td className="py-3 px-4 text-center">-</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* ページネーション */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card>
      )}

      {/* 詳細モーダル */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedPayslip(null); }}
        title={selectedPayslip ? `${selectedPayslip.year}年${selectedPayslip.month}月 給与明細` : '給与明細'}
        size="lg"
      >
        {selectedPayslip && (
          <div className="space-y-6">
            {/* 勤怠情報 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">勤怠情報</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">出勤日数</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedPayslip.workingDays || 0}日</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">勤務時間</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedPayslip.workingHours || 0}h</p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">残業時間</p>
                  <p className={`text-lg font-bold ${selectedPayslip.overtimeHours > 0 ? 'text-orange-600' : 'text-gray-900 dark:text-white'}`}>
                    {selectedPayslip.overtimeHours || 0}h
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">有給取得</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedPayslip.paidLeaveDays || 0}日</p>
                </div>
              </div>
            </div>

            {/* 支給 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">支給</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">基本給</td>
                    <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.baseSalary)}</td>
                  </tr>
                  {selectedPayslip.positionAllowance > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">役職手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.positionAllowance)}</td>
                    </tr>
                  )}
                  {selectedPayslip.overtimeAllowance > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">残業手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.overtimeAllowance)}</td>
                    </tr>
                  )}
                  {selectedPayslip.transportAllowance > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">通勤手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.transportAllowance)}</td>
                    </tr>
                  )}
                  {selectedPayslip.housingAllowance > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">住宅手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.housingAllowance)}</td>
                    </tr>
                  )}
                  {selectedPayslip.familyAllowance > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">家族手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.familyAllowance)}</td>
                    </tr>
                  )}
                  {selectedPayslip.otherAllowances > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">その他手当</td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(selectedPayslip.otherAllowances)}</td>
                    </tr>
                  )}
                  <tr className="bg-green-50 dark:bg-green-900/20">
                    <td className="py-2 font-semibold text-gray-900 dark:text-white">総支給額</td>
                    <td className="py-2 text-right font-bold text-green-600">{formatCurrency(selectedPayslip.grossSalary)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 控除 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">控除</h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">健康保険</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.healthInsurance)}</td>
                  </tr>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">厚生年金</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.pensionInsurance)}</td>
                  </tr>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">雇用保険</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.employmentInsurance)}</td>
                  </tr>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">所得税</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.incomeTax)}</td>
                  </tr>
                  <tr className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-700 dark:text-gray-300">住民税</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.residentTax)}</td>
                  </tr>
                  {selectedPayslip.otherDeductions > 0 && (
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 text-gray-700 dark:text-gray-300">その他控除</td>
                      <td className="py-2 text-right font-medium text-red-600">-{formatCurrency(selectedPayslip.otherDeductions)}</td>
                    </tr>
                  )}
                  <tr className="bg-red-50 dark:bg-red-900/20">
                    <td className="py-2 font-semibold text-gray-900 dark:text-white">控除合計</td>
                    <td className="py-2 text-right font-bold text-red-600">-{formatCurrency(selectedPayslip.totalDeductions)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 差引支給額 */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">差引支給額（手取り）</span>
                <span className="text-2xl font-bold text-blue-600">{formatCurrency(selectedPayslip.netSalary)}</span>
              </div>
              {selectedPayslip.paymentDate && (
                <p className="text-sm text-gray-500 mt-2">
                  支払日: {new Date(selectedPayslip.paymentDate).toLocaleDateString('ja-JP')}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
              {selectedPayslip.pdfUrl && (
                <Button variant="secondary" onClick={() => window.open(selectedPayslip.pdfUrl, '_blank')}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  PDFダウンロード
                </Button>
              )}
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                閉じる
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

